import { useMemo, useState } from "react";
import type { Entity, Edge } from "@/lib/intel/mock";

const TYPE_COLOR: Record<string, string> = {
  person: "oklch(0.78 0.16 195)",
  email: "oklch(0.82 0.16 75)",
  domain: "oklch(0.70 0.18 280)",
  ip: "oklch(0.72 0.18 152)",
  wallet: "oklch(0.70 0.22 25)",
  org: "oklch(0.78 0.16 195)",
  username: "oklch(0.82 0.16 75)",
  phone: "oklch(0.70 0.18 280)",
  device: "oklch(0.68 0.10 240)",
  location: "oklch(0.72 0.18 152)",
};

// Static radial layout (deterministic, no physics needed for cinematic feel)
function layout(entities: Entity[]) {
  const W = 800, H = 520;
  const cx = W / 2, cy = H / 2;
  const center = entities[0];
  const others = entities.slice(1);
  const r = 200;
  const positions: Record<string, { x: number; y: number }> = {};
  if (center) positions[center.id] = { x: cx, y: cy };
  others.forEach((e, i) => {
    const a = (i / others.length) * Math.PI * 2 - Math.PI / 2;
    const radius = r + (i % 3) * 40;
    positions[e.id] = { x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius };
  });
  return { positions, W, H };
}

export function NetworkGraph({ onSelect, entities = [] }: { onSelect?: (e: Entity) => void; entities?: Entity[] }) {
  const graphEdges: Edge[] = useMemo(
    () => entities.slice(1).map((e, i) => ({ from: entities[0]?.id ?? e.id, to: e.id, kind: i % 2 ? "correlates" : "resolves", weight: 0.7 + i * 0.02 })),
    [entities],
  );
  const { positions, W, H } = useMemo(() => layout(entities), [entities]);
  const [hover, setHover] = useState<string | null>(null);

  if (!entities.length) {
    return (
      <div className="relative h-full w-full overflow-hidden rounded-lg glass scanline grid place-items-center text-sm text-muted-foreground">
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">No entities yet</div>
          <div className="mt-2">Run an investigation to populate the graph.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden rounded-lg glass scanline">
      <div className="absolute inset-0 grid-bg-fine opacity-30" />
      <div className="absolute left-3 top-3 z-10 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-cyan pulse-dot" />
        Live entity graph · {entities.length} nodes · {graphEdges.length} edges
      </div>
      <div className="absolute right-3 top-3 z-10 font-mono text-[10px] text-muted-foreground">
        ZOOM 1.00× · LAYOUT FORCE-RADIAL
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full">
        <defs>
          <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0" stopColor="oklch(0.78 0.16 195)" stopOpacity="0.4" />
            <stop offset="1" stopColor="oklch(0.78 0.16 195)" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx={W/2} cy={H/2} r={240} fill="url(#centerGlow)" />
        <circle cx={W/2} cy={H/2} r={200} fill="none" stroke="oklch(0.78 0.16 195 / 0.15)" strokeDasharray="2 6" />
        <circle cx={W/2} cy={H/2} r={280} fill="none" stroke="oklch(0.78 0.16 195 / 0.08)" strokeDasharray="2 6" />

        {graphEdges.map((e, i) => {
          const a = positions[e.from], b = positions[e.to];
          if (!a || !b) return null;
          const active = hover === e.from || hover === e.to;
          return (
            <g key={i}>
              <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={active ? "oklch(0.85 0.18 195)" : "oklch(0.70 0.05 240 / 0.4)"}
                strokeWidth={active ? 1.4 : 0.8} />
              <text x={(a.x+b.x)/2} y={(a.y+b.y)/2 - 4}
                fontSize="8" fontFamily="JetBrains Mono"
                fill={active ? "oklch(0.85 0.18 195)" : "oklch(0.55 0.05 240)"}
                textAnchor="middle" opacity={active ? 1 : 0.6}>
                {e.kind}
              </text>
            </g>
          );
        })}

        {entities.map((e) => {
          const p = positions[e.id];
          const isCenter = e.id === entities[0]?.id;
          const r = isCenter ? 22 : 12;
          const color = TYPE_COLOR[e.type] || "white";
          const active = hover === e.id;
          return (
            <g key={e.id} transform={`translate(${p.x},${p.y})`}
               onMouseEnter={() => setHover(e.id)}
               onMouseLeave={() => setHover(null)}
               onClick={() => onSelect?.(e)}
               style={{ cursor: "pointer" }}>
              {active && <circle r={r + 8} fill={color} opacity="0.15" />}
              <circle r={r + 2} fill="oklch(0.16 0.018 240)" stroke={color} strokeWidth={isCenter ? 2 : 1.2} />
              <circle r={r - 4} fill={color} opacity={isCenter ? 0.9 : 0.7} />
              <text y={r + 14} fontSize={isCenter ? 11 : 9} fontFamily="Inter" fontWeight="600"
                fill="oklch(0.96 0 0)" textAnchor="middle">
                {e.label.length > 22 ? e.label.slice(0, 20) + "…" : e.label}
              </text>
              <text y={r + 25} fontSize="8" fontFamily="JetBrains Mono"
                fill="oklch(0.65 0.02 240)" textAnchor="middle">
                {e.type.toUpperCase()} · {e.confidence}%
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-muted-foreground">
        {Object.entries(TYPE_COLOR).slice(0, 6).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: v }} /> {k}
          </span>
        ))}
      </div>
    </div>
  );
}
